import { generateWorld, generateRoom, center, cellAt, type DungeonNode, type Exit } from './dungeon.ts';
import { ROOMS, type RoomKind } from './rooms.ts';
import { makeEnemy, updateEnemies } from './ai.ts';
export { ROOMS };
export const W = 640, H = 360, TILE = 32;
export const GOBLIN_RADIUS=6, PICKAXE_DWARF_DAMAGE=.12, BOMB_RANGE=4;
export const NAMES = ['GRUB', 'MOG', 'NIB', 'ZOG'];
export const COLORS = ['#c2eb72', '#7ed9e3', '#e7af6b', '#da96da'];
export type Input = { x: number; y: number; a: boolean; b: boolean; interact: boolean; dash: boolean; swap: boolean };
export const emptyInput = (): Input => ({ x: 0, y: 0, a: false, b: false, interact: false, dash: false, swap: false });
export type Player = { id: number; x: number; y: number; dx: number; dy: number; hp: number; maxHp: number; heartPieces: number; blackHearts: number; inv: number; cooldown: number; dash: number; dashCooldown: number; bombs: number; knives: number; weapon: 'bomb' | 'knife'; carrying: number | null; moving: boolean; swing: number; previous: Input; walk: number; action: number; hurt: number; prevX:number; prevY:number };
export type PropKind = 'crate' | 'barrel' | 'sack' | 'table' | 'bench' | 'shelf' | 'vat' | 'anvil' | 'powder' | 'cart' | 'chest';
export type Prop = { id: number; kind: PropKind; x: number; y: number; hp: number; vx: number; vy: number; carried: boolean; thrown: number; fuse?:number };
export type EnemyKind = 'guard' | 'archer' | 'thrower' | 'brute' | 'rat' | 'bat';
export type EnemyMode = 'patrol' | 'chase' | 'aim' | 'windup' | 'recover' | 'retrieve' | 'charge' | 'evade';
export type Enemy = { id: number; kind: EnemyKind; x: number; y: number; hp: number; maxHp: number; hit: number; attack: number; dx: number; dy: number; moving: boolean; walk: number; mode: EnemyMode; timer: number; duration: number; aimX: number; aimY: number; axeId: number | null; path: number[]; repath: number; alert: number; homeX: number; homeY: number; lastX:number; lastY:number };
export type Projectile = { id: number; kind: 'bomb' | 'knife' | 'arrow' | 'axe'; x: number; y: number; vx: number; vy: number; life: number; owner: number; enemy?: boolean; stuck?: boolean; stuckOn?: number; offsetX?: number; offsetY?: number; angle?: number };
export type Decal = {x: number; y: number; kind: 'wood' | 'beer' | 'scorch'; size: number};
export type Pickup = { id: number; x: number; y: number; kind: 'food' | 'beer' | 'ammo' | 'heart' | 'heartPiece' | 'blackHeart'; phase: number };
export type Particle = { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number };
export type Blast={id:number;cells:number[];life:number;hit:string[]};
export type Reinforcement={cell:number;timer:number;count:number};
export type RoomMemory={tiles:number[];tileHp:number[];floor:number[];props:Prop[];enemies:Enemy[];projectiles:Projectile[];pickups:Pickup[];decals:Decal[];blasts:Blast[];treasureCell:number|null;spawnCells:number[];savedAt:number;revision:number};
export type State = {
  players:Player[];props:Prop[];enemies:Enemy[];projectiles:Projectile[];pickups:Pickup[];particles:Particle[];
  tiles:number[];tileHp:number[];floor:number[];decals:Decal[];blasts:Blast[];theme:RoomKind;roomId:number;room:number;revision:number;
  world:DungeonNode[];memories:Record<number,RoomMemory>;exits:Exit[];opened:string[];visited:number[];spawnCells:number[];treasureCell:number|null;prize:boolean;
  alarm:number;reinforcementIn:number;reinforcement:Reinforcement|null;waves:number;travelCooldown:number;
  loot:number;beer:number;time:number;shake:number;phase:'menu'|'playing'|'paused'|'won'|'lost';notice:string;noticeId:number;sound:number;soundKind:string;seed:number;nextId:number;
};
export function random(s: State) { s.seed = (Math.imul(s.seed, 1664525) + 1013904223) >>> 0; return s.seed / 4294967296; }
export function player(id: number): Player { return { id, x: 90 + id * 24, y: 180, dx: 1, dy: 0, hp: 3, maxHp: 3, heartPieces: 0, blackHearts: 0, inv: 0, cooldown: 0, dash: 0, dashCooldown: 0, bombs: 5, knives: 12, weapon: 'bomb', carrying: null, moving: false, swing: 0, previous: emptyInput(), walk: 0, action: 0, hurt: 0, prevX:90+id*24, prevY:180 }; }
export function createState(count=1,seed=42):State {
  const s:State={players:Array.from({length:count},(_,i)=>player(i)),props:[],enemies:[],projectiles:[],pickups:[],particles:[],tiles:[],tileHp:[],floor:[],decals:[],blasts:[],theme:'pantry',roomId:0,room:0,revision:0,world:generateWorld(seed),memories:{},exits:[],opened:[],visited:[],spawnCells:[],treasureCell:null,prize:false,alarm:0,reinforcementIn:7,reinforcement:null,waves:0,travelCooldown:0,loot:0,beer:0,time:0,shake:0,phase:'menu',notice:'',noticeId:0,sound:0,soundKind:'',seed,nextId:0};
  buildRoom(s);return s;
}
export function notice(s:State,message:string){s.notice=message;s.noticeId++;}
function sound(s:State,kind:string){s.soundKind=kind;s.sound++;}
export const PROP_STATS:Record<PropKind,{hp:number;radius:number;size:number;fixed:boolean}>={
  crate:{hp:2,radius:10,size:30,fixed:false},barrel:{hp:3,radius:10,size:30,fixed:false},sack:{hp:2,radius:9,size:28,fixed:false},
  table:{hp:4,radius:11,size:31,fixed:true},bench:{hp:2,radius:10,size:30,fixed:false},shelf:{hp:4,radius:11,size:31,fixed:true},
  vat:{hp:5,radius:11,size:31,fixed:true},anvil:{hp:7,radius:10,size:30,fixed:true},powder:{hp:1,radius:10,size:30,fixed:false},
  cart:{hp:4,radius:11,size:31,fixed:false},chest:{hp:4,radius:10,size:30,fixed:false},
};
export function propRadius(p:Prop){return PROP_STATS[p.kind].radius;}
export function raiseAlarm(s:State,amount=.2){
  const quiet=s.alarm===0;s.alarm=Math.min(5,Math.max(1,s.alarm)+amount);
  if(quiet){s.reinforcementIn=4;notice(s,'ALLARME! I rinforzi non smetteranno di arrivare. Nasconditi e trova un’altra strada.');}
}
export function buildRoom(s:State,from?:number){
  const node=s.world[s.room]!;s.theme=node.theme;s.exits=node.exits.map(e=>({...e}));s.roomId++;s.particles=[];s.reinforcement=null;
  const memory=s.memories[s.room];
  if(memory){
    for(const key of ['tiles','tileHp','floor','props','enemies','projectiles','pickups','decals','blasts','treasureCell','spawnCells','revision'] as const)(s as any)[key]=structuredClone(memory[key]);
    // Fuses continue to elapse while the room is empty; resolve them before placing the party.
    const party=s.players;s.players=[];
    let elapsed=Math.min(5,s.time-memory.savedAt);
    while(elapsed>0){const dt=Math.min(.04,elapsed);updateProjectiles(s,dt);updateExplosives(s,dt);updateBlasts(s,dt);elapsed-=dt;}
    s.players=party;
  }else{
    const room=generateRoom(node);s.tiles=room.tiles;s.floor=room.floor;s.tileHp=s.tiles.map(t=>t===4?3:t===3?99:0);s.revision=0;
    s.props=room.props.map(({kind,cell})=>({id:s.nextId++,kind,...center(cell),hp:PROP_STATS[kind].hp,vx:0,vy:0,carried:false,thrown:0}));
    s.enemies=room.enemies.map(({kind,cell})=>makeEnemy(s,kind,center(cell).x,center(cell).y));
    s.projectiles=[];s.pickups=[];s.decals=[];s.blasts=[];s.treasureCell=room.treasureCell;s.spawnCells=room.spawnCells;
  }
  for(const exit of s.exits)if(s.opened.includes(exit.key)){s.tiles[exit.cell]=0;s.tileHp[exit.cell]=0;}
  if(s.prize&&s.theme==='vault')s.treasureCell=null;
  const arrival=from===undefined?{x:112,y:176}:center(s.exits.find(e=>e.to===from)?.cell??103);
  const free=s.tiles.map((t,i)=>t===0&&!s.props.some(o=>distance(o,center(i))<propRadius(o)+GOBLIN_RADIUS)?i:-1).filter(i=>i>=0).sort((a,b)=>distance(center(a),arrival)-distance(center(b),arrival));
  for(let i=0;i<s.players.length;i++){
    const p=s.players[i]!,point=center(free[Math.min(i,free.length-1)]??103);p.x=point.x;p.y=point.y;p.prevX=p.x;p.prevY=p.y;p.inv=.8;p.walk=0;p.hurt=0;p.previous=emptyInput();p.moving=false;
  }
  if(!s.visited.includes(s.room))s.visited.push(s.room);s.travelCooldown=.8;
  notice(s,`${node.theme==='vault'?'LA RISERVA! ':''}${ROOMS[s.theme].name} · E sui passaggi per cambiare stanza.`);
}
export function saveRoom(s:State){
  s.memories[s.room]=structuredClone({tiles:s.tiles,tileHp:s.tileHp,floor:s.floor,props:s.props,enemies:s.enemies,projectiles:s.projectiles,pickups:s.pickups,decals:s.decals,blasts:s.blasts,treasureCell:s.treasureCell,spawnCells:s.spawnCells,savedAt:s.time,revision:s.revision});
}
export function enterRoom(s:State,to:number){
  const from=s.room;
  const held=s.players.map(p=>({p,prop:s.props.find(o=>o.id===p.carrying)}));
  s.props=s.props.filter(o=>!o.carried);saveRoom(s);s.room=to;buildRoom(s,from);
  for(const {p,prop} of held)if(prop){prop.x=p.x;prop.y=p.y;s.props.push(prop);p.carrying=prop.id;}
}
export function damageTile(s:State,cell:number,blast=false){
  const tile=s.tiles[cell];if(tile!==4&&!(blast&&tile===3))return;
  s.tileHp[cell]=blast?0:(s.tileHp[cell]||3)-1;
  const point=center(cell);burst(s,point.x,point.y,tile===3?'#b18b53':'#b4ad92',8);raiseAlarm(s,.04);
  if(s.tileHp[cell]!<=0){s.tiles[cell]=0;s.revision++;const exit=s.exits.find(e=>e.cell===cell);if(exit&&!s.opened.includes(exit.key))s.opened.push(exit.key);}
}
export function tryTravel(s:State,p:Player):boolean {
  const exit=s.exits.find(e=>s.tiles[e.cell]===0&&distance(p,center(e.cell))<23);
  if(!exit||s.travelCooldown>0)return false;
  if(s.players.some(other=>other.hp>0&&distance(other,center(exit.cell))>85)){notice(s,'Raduna la banda vicino al passaggio.');return true;}
  if(exit.to===-1){if(s.prize){s.phase='won';sound(s,'win');notice(s,'La riserva è tua. La fortezza sta ancora esplodendo.');}else notice(s,'Trova il barile d’oro nella riserva, poi torna qui per fuggire.');}
  else enterRoom(s,exit.to);
  return true;
}
export const distance = (a: {x:number;y:number}, b: {x:number;y:number}) => Math.hypot(a.x-b.x,a.y-b.y);
export function blocked(s: State, x: number, y: number, radius = GOBLIN_RADIUS): boolean {
  for (const ox of [-radius,radius]) for (const oy of [-radius,radius]) {
    const tx = Math.floor((x+ox)/TILE), ty = Math.floor((y+oy)/TILE);
    if (tx < 1 || tx > 18 || ty < 2 || ty > 9 || s.tiles[ty*20+tx]) return true;
  } return false;
}
export function move(s: State, entity: {x:number;y:number}, dx: number, dy: number, radius = GOBLIN_RADIUS) {
  const steps = Math.max(1,Math.ceil(Math.max(Math.abs(dx), Math.abs(dy))/5));
  for(let i=0;i<steps;i++) { if(!blocked(s,entity.x+dx/steps,entity.y,radius)) entity.x+=dx/steps; if(!blocked(s,entity.x,entity.y+dy/steps,radius)) entity.y+=dy/steps; }
}
export function obstructed(s:State,x:number,y:number,radius=8,ignore?:Prop):boolean {
  return blocked(s,x,y,radius)||s.props.some(o=>o!==ignore&&!o.carried&&Math.hypot(o.x-x,o.y-y)<propRadius(o)+radius-1);
}
export function lineClear(s:State,a:{x:number;y:number},b:{x:number;y:number},radius=2,ignoreProps=false):boolean {
  const d=distance(a,b),steps=Math.ceil(d/5);
  for(let i=1;i<=steps;i++){const x=a.x+(b.x-a.x)*i/steps,y=a.y+(b.y-a.y)*i/steps;if(blocked(s,x,y,radius)||(!ignoreProps&&s.props.some(o=>!o.carried&&Math.hypot(o.x-x,o.y-y)<propRadius(o)+radius)))return false;}
  return true;
}
export function moveActor(s:State,e:{x:number;y:number},dx:number,dy:number,radius=8,flying=false){
  const steps=Math.max(1,Math.ceil(Math.max(Math.abs(dx),Math.abs(dy))/4));
  for(let i=0;i<steps;i++){
    const clear=(x:number,y:number)=>flying?!blocked(s,x,y,radius):!obstructed(s,x,y,radius);
    if(clear(e.x+dx/steps,e.y))e.x+=dx/steps;if(clear(e.x,e.y+dy/steps))e.y+=dy/steps;
  }
}
export function burst(s: State, x: number, y: number, color: string, count = 12) {
  for(let i=0;i<count;i++) { const angle=random(s)*Math.PI*2,speed=20+random(s)*80, life=.25+random(s)*.5; s.particles.push({x,y,vx:Math.cos(angle)*speed,vy:Math.sin(angle)*speed,life,max:life,color,size:1+Math.floor(random(s)*3)}); }
}
function containerLoot(s:State,kind:PropKind,index:number):Pickup['kind']{
  // Keep bomb resupply reliable, including when a container rolls a health pickup.
  if(index===0&&(kind==='crate'||kind==='chest'||kind==='anvil')||kind==='chest'&&index===1)return 'ammo';
  const roll=random(s);
  if(roll<.16)return 'heart';
  if(roll<.26)return 'heartPiece';
  if(roll<.33)return 'blackHeart';
  return kind==='barrel'||kind==='vat'?'beer':kind==='chest'||kind==='anvil'?'ammo':'food';
}
export function destroy(s: State, prop: Prop) {
  if(!s.props.includes(prop)) return;
  s.props = s.props.filter(p=>p!==prop);
  for(const p of s.players) if(p.carrying===prop.id) p.carrying=null;
  burst(s,prop.x,prop.y,prop.kind==='barrel'?'#d49a46':'#ae8050',18);
  s.decals.push({x:prop.x,y:prop.y,kind:prop.kind==='barrel'||prop.kind==='vat'?'beer':prop.kind==='powder'?'scorch':'wood',size:PROP_STATS[prop.kind].size*.7});s.decals=s.decals.slice(-80);
  const amount=prop.kind==='chest'?4:prop.kind==='powder'?0:2;
  for(let i=0;i<amount;i++) s.pickups.push({id:s.nextId++,x:Math.min(590,Math.max(48,prop.x+(random(s)-.5)*22)),y:Math.min(307,Math.max(78,prop.y+(random(s)-.5)*22)),kind:containerLoot(s,prop.kind,i),phase:random(s)*6});
  sound(s,'break');
  if(prop.kind==='powder')explode(s,{id:s.nextId++,kind:'bomb',x:prop.x,y:prop.y,vx:0,vy:0,life:0,owner:-1});
}
export function damageEnemy(s: State, e: Enemy, damage: number, dx: number, dy: number) {
  if(e.hp<=0) return; e.hp-=damage;e.hit=damage<.5?0:.22;e.alert=7;raiseAlarm(s,.1);const knock=damage<.5?0:e.kind==='brute'?2:10;moveActor(s,e,dx*knock,dy*knock,e.kind==='brute'?14:8,e.kind==='bat');if(damage>=.5&&(e.mode==='aim'||e.mode==='windup')){e.mode='recover';e.timer=.35;} burst(s,e.x,e.y-8,'#e8b777',6);
  if(e.hp<=0) { s.pickups.push({id:s.nextId++,x:e.x,y:e.y,kind:'beer',phase:0}); sound(s,'break'); }
}
export function damagePlayer(s: State,p: Player,damage:number) {
  if(p.inv>0||p.hp<=0||damage<=0) return;
  const absorbed=Math.min(p.blackHearts,damage);p.blackHearts-=absorbed;p.hp=Math.max(0,p.hp-(damage-absorbed));
  p.inv=.65;p.hurt=.25; s.shake=4; burst(s,p.x,p.y,absorbed>0?'#b6a2d2':'#d89570',8); sound(s,'hurt');
  if(p.hp<=0) { const held=s.props.find(o=>o.id===p.carrying); if(held) {held.carried=false; held.x=p.x;held.y=p.y;}p.carrying=null;notice(s,`${NAMES[p.id]} è a terra! Avvicinati e premi Prendi.`); }
}
export function collectPickup(s:State,p:Player,item:Pickup):boolean{
  if(p.hp<=0||!s.pickups.includes(item)||item.kind==='heart'&&p.hp>=p.maxHp)return false;
  if(item.kind==='heart')p.hp=Math.min(p.maxHp,p.hp+1);
  else if(item.kind==='heartPiece'){
    p.heartPieces++;
    if(p.heartPieces===4){p.heartPieces=0;p.maxHp++;p.hp++;notice(s,`${NAMES[p.id]} · Cuore completo! ${p.maxHp} cuori massimi.`);}
    else notice(s,`${NAMES[p.id]} · Frammento di cuore ${p.heartPieces}/4.`);
  }else if(item.kind==='blackHeart'){p.blackHearts++;notice(s,`${NAMES[p.id]} · +1 cuore nero: assorbe i prossimi colpi.`);}
  else if(item.kind==='food')p.hp=Math.min(p.maxHp,p.hp+.1);
  else if(item.kind==='beer')s.beer++;
  else if(item.kind==='ammo'){p.bombs=Math.min(12,p.bombs+2);p.knives=Math.min(24,p.knives+3);}
  s.pickups=s.pickups.filter(i=>i!==item);s.loot++;
  burst(s,item.x,item.y,item.kind==='heart'?'#f08082':item.kind==='blackHeart'?'#b6a2d2':'#ddda8b',5);sound(s,'pickup');return true;
}
function throwHeld(s:State,p:Player) {
  const prop=s.props.find(o=>o.id===p.carrying);p.carrying=null;if(!prop) return;
  prop.carried=false;prop.x=p.x;prop.y=p.y;prop.vx=p.dx*260;prop.vy=p.dy*260;prop.thrown=.8;p.action=.3;sound(s,'swing');
}
export function useSlot(s:State,p:Player,slot:number,input:Input) {
  if(p.cooldown>0||p.hp<=0)return;
  if(p.carrying!==null){throwHeld(s,p);p.cooldown=.3;return;}
  if(slot===0) {
    p.cooldown=.32;p.swing=.26;p.action=.26;sound(s,'swing');
    const target={x:p.x+p.dx*20,y:p.y+p.dy*20};
    for(const o of [...s.props]) if(!o.carried&&distance(target,o)<20+propRadius(o)){o.hp--;if(!PROP_STATS[o.kind].fixed){o.vx+=p.dx*65;o.vy+=p.dy*65;}burst(s,o.x,o.y,'#bf9b65',4);if(o.hp<=0)destroy(s,o);}
    for(const e of s.enemies)if(distance(target,e)<25&&lineClear(s,p,e,1,true))damageEnemy(s,e,e.kind==='rat'||e.kind==='bat'?.7:e.kind==='brute'?.06:PICKAXE_DWARF_DAMAGE,p.dx,p.dy);
    const cell=cellAt(p.x+p.dx*25,p.y+p.dy*25);damageTile(s,cell);raiseAlarm(s,.015);
  }else{
    if(p.weapon==='bomb'&&p.bombs<=0||p.weapon==='knife'&&p.knives<=0){p.cooldown=.4;notice(s,'Tasche vuote! Cerca munizioni nelle casse o premi Q.');return;}
    const bomb=p.weapon==='bomb',moving=Math.hypot(input.x,input.y)>.15,speed=bomb?(moving?160:0):320;
    if(bomb)p.bombs--;else p.knives--;
    s.projectiles.push({id:s.nextId++,kind:p.weapon,x:p.x+p.dx*12,y:p.y+p.dy*12,vx:p.dx*speed,vy:p.dy*speed,life:bomb?1.75:1.6,owner:p.id});
    p.action=.25;p.cooldown=bomb?.45:.24;sound(s,'swing');
  }
}
/** Same cell list drives preview, chain propagation and damage. Solid masonry stops before
 * the cell; destructible rock, doors and containers absorb the final flame tile. */
export function blastCells(s:State,point:{x:number;y:number},range=BOMB_RANGE):number[]{
  const origin=cellAt(point.x,point.y),cells=[origin];
  for(const [dx,dy] of [[1,0],[-1,0],[0,1],[0,-1]])for(let n=1;n<=range;n++){
    const x=origin%20+dx!*n,y=Math.floor(origin/20)+dy!*n;if(x<1||x>18||y<2||y>9)break;
    const cell=y*20+x,tile=s.tiles[cell];if(tile===1||tile===2)break;cells.push(cell);
    if(tile===3||tile===4||s.props.some(p=>!p.carried&&cellAt(p.x,p.y)===cell))break;
  }
  return cells;
}
export function explode(s:State,b:Projectile){
  const cells=blastCells(s,b);s.blasts.push({id:s.nextId++,cells,life:.55,hit:[]});
  burst(s,b.x,b.y,'#ffd17c',25);s.shake=Math.min(12,s.shake+6);sound(s,'boom');raiseAlarm(s,.35);
  for(const cell of cells){const point=center(cell);burst(s,point.x,point.y,'#ed843e',4);damageTile(s,cell,true);}
  for(const prop of [...s.props])if(cells.includes(cellAt(prop.x,prop.y))){if(prop.kind==='powder')prop.fuse=Math.min(prop.fuse??99,.09);else destroy(s,prop);}
  for(const other of s.projectiles)if(other!==b&&other.kind==='bomb'&&cells.includes(cellAt(other.x,other.y)))other.life=Math.min(other.life,.09);
  updateBlasts(s,0);
}
function inBlast(cells:number[],actor:{x:number;y:number},radius:number){return cells.some(cell=>{const c=center(cell);return Math.abs(actor.x-c.x)<16+radius&&Math.abs(actor.y-c.y)<16+radius;});}
export function updateBlasts(s:State,dt:number){
  for(const blast of s.blasts){
    blast.life-=dt;
    for(const p of s.players)if(p.hp>0&&!blast.hit.includes(`p${p.id}`)&&inBlast(blast.cells,p,GOBLIN_RADIUS)){damagePlayer(s,p,2);blast.hit.push(`p${p.id}`);}
    for(const e of s.enemies)if(e.hp>0&&!blast.hit.includes(`e${e.id}`)&&inBlast(blast.cells,e,e.kind==='brute'?13:9)){damageEnemy(s,e,7,0,0);blast.hit.push(`e${e.id}`);}
  }
  s.blasts=s.blasts.filter(b=>b.life>0);
}
export function updateExplosives(s:State,dt:number){for(const p of [...s.props])if(p.fuse!==undefined){p.fuse-=dt;if(p.fuse<=0)destroy(s,p);}}
export function updateReinforcements(s:State,dt:number){
  if(s.alarm<=0)return;
  if(s.reinforcement){
    s.reinforcement.timer-=dt;
    if(s.reinforcement.timer<=0){
      const pad=center(s.reinforcement.cell),candidates=s.tiles.map((t,i)=>t===0?i:-1).filter(i=>i>=0&&distance(center(i),pad)<54&&!obstructed(s,center(i).x,center(i).y,10)&&!s.players.some(p=>distance(p,center(i))<34)).sort((a,b)=>distance(center(a),pad)-distance(center(b),pad));
      for(let i=0;i<s.reinforcement.count&&s.enemies.length<18;i++){
        const cell=candidates[i];if(cell===undefined)break;
        const kinds:EnemyKind[]=s.alarm>2?['guard','thrower','archer','brute']:['guard','archer','thrower'];
        const e=makeEnemy(s,kinds[(s.waves+i)%kinds.length]!,center(cell).x,center(cell).y);e.alert=3;s.enemies.push(e);
      }
      s.waves++;s.reinforcement=null;s.reinforcementIn=Math.max(2.8,6-s.alarm*.55);
    }
  }else{
    s.reinforcementIn-=dt;
    if(s.reinforcementIn<=0&&s.enemies.length<18){
      const pads=s.spawnCells.filter(c=>s.tiles[c]===0);const cell=pads[Math.floor(random(s)*pads.length)];
      if(cell!==undefined)s.reinforcement={cell,timer:1.6,count:Math.min(3,1+Math.floor(s.alarm))};
    }
  }
}
export function update(s:State,inputs:Map<number,Input>,dt:number){
  if(s.phase!=='playing')return;
  dt=Math.min(dt,.04);s.time+=dt;s.travelCooldown=Math.max(0,s.travelCooldown-dt);s.shake=Math.max(0,s.shake-dt*22);
  for(const p of s.players){
    const input=inputs.get(p.id)||emptyInput();p.prevX=p.x;p.prevY=p.y;
    p.cooldown=Math.max(0,p.cooldown-dt);p.inv=Math.max(0,p.inv-dt);p.swing=Math.max(0,p.swing-dt);p.action=Math.max(0,p.action-dt);p.hurt=Math.max(0,p.hurt-dt);p.dashCooldown=Math.max(0,p.dashCooldown-dt);p.dash=Math.max(0,p.dash-dt);
    if(p.hp<=0){p.moving=false;p.previous={...input};continue;}
    let {x,y}=input;const len=Math.hypot(x,y);if(len>1){x/=len;y/=len;}p.moving=len>.1;
    if(p.moving){p.dx=x/(Math.hypot(x,y)||1);p.dy=y/(Math.hypot(x,y)||1);}
    if(input.dash&&!p.previous.dash&&p.dashCooldown===0){p.dash=.16;p.dashCooldown=.85;sound(s,'swing');}
    const slick=s.decals.some(d=>d.kind==='beer'&&distance(d,p)<d.size*.55);
    const speed=(p.dash>0?280:p.carrying!==null?70:97)*(slick&&p.dash<=0?.75:1);
    const old={x:p.x,y:p.y};move(s,p,(p.dash>0?p.dx:x)*speed*dt,(p.dash>0?p.dy:y)*speed*dt);
    for(const o of s.props)if(!o.carried&&distance(p,o)<propRadius(o)+GOBLIN_RADIUS){
      const before={x:o.x,y:o.y};
      if(!PROP_STATS[o.kind].fixed){const nx=o.x+(p.x-old.x)*.85,ny=o.y+(p.y-old.y)*.85;if(!obstructed(s,nx,ny,propRadius(o),o)){o.x=nx;o.y=ny;}}
      if(distance(p,o)<propRadius(o)+GOBLIN_RADIUS-1||distance(before,o)<.001){p.x=old.x;p.y=old.y;}
    }
    const traveled=distance(p,old);p.moving=traveled>.01;p.walk+=traveled/9;
    if(p.dash>0&&Math.floor(s.time*60)%3===0)burst(s,p.x-p.dx*8,p.y-p.dy*8,COLORS[p.id]!,1);
    if(input.swap&&!p.previous.swap){p.weapon=p.weapon==='bomb'?'knife':'bomb';sound(s,'pickup');}
    if(input.interact&&!p.previous.interact){
      if(s.treasureCell!==null&&distance(p,center(s.treasureCell))<28){s.prize=true;s.treasureCell=null;raiseAlarm(s,2);notice(s,'BARILE D’ORO RUBATO! Torna all’ingresso della fortezza.');}
      else if(tryTravel(s,p)){p.previous={...input};return;}
      const down=s.players.find(other=>other!==p&&other.hp<=0&&distance(other,p)<35);
      if(down){down.hp=Math.min(2,down.maxHp);down.blackHearts=0;down.inv=2;notice(s,`${NAMES[down.id]} torna a fare danni!`);sound(s,'pickup');}
      else if(p.carrying!==null){const o=s.props.find(o=>o.id===p.carrying);if(o){const reach=propRadius(o)+13,nx=p.x+p.dx*reach,ny=p.y+p.dy*reach;if(obstructed(s,nx,ny,propRadius(o),o)){notice(s,'Non c’è spazio per posarlo qui.');}else{o.carried=false;o.x=nx;o.y=ny;}}if(!o?.carried)p.carrying=null;}
      else{const o=s.props.filter(o=>!o.carried&&!PROP_STATS[o.kind].fixed&&distance(p,o)<propRadius(o)+25).sort((a,b)=>distance(a,p)-distance(b,p))[0];if(o){p.carrying=o.id;o.carried=true;p.action=.2;sound(s,'pickup');}}
    }
    if(input.a)useSlot(s,p,0,input);else if(input.b)useSlot(s,p,1,input);
    const held=s.props.find(o=>o.id===p.carrying);if(held){held.x=p.x;held.y=p.y;}
    for(const item of [...s.pickups])if(distance(p,item)<22)collectPickup(s,p,item);
    p.previous={...input};
  }
  for(const o of [...s.props])if(!o.carried){
    const before={x:o.x,y:o.y};move(s,o,o.vx*dt,o.vy*dt,propRadius(o));o.vx*=Math.exp(-5*dt);o.vy*=Math.exp(-5*dt);
    if(o.thrown>0){o.thrown-=dt;let hit=false;
      for(const e of s.enemies)if(e.hp>0&&distance(e,o)<24){damageEnemy(s,e,3,o.vx/150,o.vy/150);hit=true;}
      for(const other of [...s.props])if(other!==o&&!other.carried&&distance(other,o)<propRadius(other)+propRadius(o)){other.hp-=2;if(other.hp<=0)destroy(s,other);hit=true;}
      if((Math.abs(o.vx)+Math.abs(o.vy)>30&&distance(before,o)<.1)||hit){destroy(s,o);s.shake=3;}
    }
  }
  updateEnemies(s,dt);
  updateReinforcements(s,dt);
  s.enemies=s.enemies.filter(e=>e.hp>0);
  updateProjectiles(s,dt);
  updateExplosives(s,dt);updateBlasts(s,dt);
  for(const p of s.particles){p.life-=dt;p.x+=p.vx*dt;p.y+=p.vy*dt;p.vx*=.94;p.vy*=.94;}s.particles=s.particles.filter(p=>p.life>0).slice(-400);
  if(s.players.every(p=>p.hp<=0)){s.phase='lost';notice(s,'La banda è stata cacciata. La birra resta ai nani.');return;}
}

export function lodgeProjectile(s:State,b:Projectile,prop?:Prop){
  b.stuck=true;b.angle=Math.atan2(b.vy,b.vx);b.vx=0;b.vy=0;b.life=b.kind==='axe'?3600:4;
  if(prop){b.stuckOn=prop.id;b.offsetX=b.x-prop.x;b.offsetY=b.y-prop.y;}
  burst(s,b.x,b.y,'#c9b38a',4);
}
export function updateProjectiles(s:State,dt:number){
  for(const b of [...s.projectiles]){
    b.life-=dt;
    if(b.stuck){
      if(b.stuckOn!==undefined){const prop=s.props.find(o=>o.id===b.stuckOn);if(prop){b.x=prop.x+(b.offsetX||0);b.y=prop.y+(b.offsetY||0);}else b.stuckOn=undefined;}
      if(b.kind==='axe'&&!s.enemies.some(e=>e.id===b.owner))b.life=Math.min(b.life,4);
    }else{
      const steps=Math.max(1,Math.ceil(Math.hypot(b.vx,b.vy)*dt/4));
      for(let i=0;i<steps&&b.life>0&&!b.stuck;i++){
        const nx=b.x+b.vx*dt/steps,ny=b.y+b.vy*dt/steps;
        if(blocked(s,nx,ny,3)){
          if(b.kind==='bomb'){if(blocked(s,nx,b.y,3))b.vx*=-.5;if(blocked(s,b.x,ny,3))b.vy*=-.5;break;}
          if(b.enemy)lodgeProjectile(s,b);else b.life=0;break;
        }
        const ox=b.x,oy=b.y;b.x=nx;b.y=ny;
        const prop=s.props.find(o=>!o.carried&&distance(o,b)<propRadius(o)+2);
        if(prop&&b.kind!=='bomb'){
          if(b.enemy)lodgeProjectile(s,b,prop);else{prop.hp--;if(prop.hp<=0)destroy(s,prop);b.life=0;}break;
        }
        if(b.enemy){
          const target=s.players.find(p=>p.hp>0&&sweptHit(ox,oy,nx,ny,p.prevX+(p.x-p.prevX)*i/steps,p.prevY+(p.y-p.prevY)*i/steps,p.prevX+(p.x-p.prevX)*(i+1)/steps,p.prevY+(p.y-p.prevY)*(i+1)/steps,GOBLIN_RADIUS+(b.kind==='axe'?8:4)));
          const ally=s.enemies.find(e=>e.hp>0&&e.id!==b.owner&&sweptHit(ox,oy,nx,ny,e.x,e.y,e.x,e.y,e.kind==='brute'?16:11));
          if(target||ally){if(target)damagePlayer(s,target,b.kind==='axe'?1.5:1);else if(ally)damageEnemy(s,ally,b.kind==='axe'?2.5:1.5,0,0);if(b.kind==='axe')lodgeProjectile(s,b);else b.life=0;break;}
        }else if(b.kind==='knife'){
          const enemy=s.enemies.find(e=>e.hp>0&&distance(e,b)<(e.kind==='brute'?22:16));
          if(enemy){damageEnemy(s,enemy,2,b.vx/300,b.vy/300);b.life=0;break;}
        }
      }
      if(b.kind==='bomb'){b.vx*=Math.exp(-2.2*dt);b.vy*=Math.exp(-2.2*dt);}
      if(b.life<=0&&b.kind==='axe'&&!b.stuck)lodgeProjectile(s,b);
    }
    if(b.life<=0){if(b.kind==='bomb')explode(s,b);s.projectiles=s.projectiles.filter(p=>p!==b);}
  }
}

export function sweptHit(ax:number,ay:number,bx:number,by:number,px:number,py:number,qx:number,qy:number,radius:number){
  const x=ax-px,y=ay-py,dx=(bx-ax)-(qx-px),dy=(by-ay)-(qy-py),t=Math.max(0,Math.min(1,-(x*dx+y*dy)/(dx*dx+dy*dy||1)));
  return Math.hypot(x+dx*t,y+dy*t)<=radius;
}
